import React, { useState, useContext } from "react";
import { useSelector, useDispatch } from "react-redux";
import { get } from "lodash";
import styled from "styled-components";

import { Card, CardTitle, CardDescription, CardButton, SecondaryCardButton, ConsentSpan } from "./Cards";

import GaReporter, { GaEcommerceAction } from "../../utilities/gaReporter";
import { SET_ERROR } from "../../store/actions/errorActions";
import {
    getFrontendSettings,
    getSessionId,
    getShowPrice,
    getGuiTranslations,
    getAppCustomization,
    getAppConfig,
    getLanguage,
    getTouchScreen,
    getCurrentSummaries,
    getTotalPrice,
    getTotalPriceText,
    getConfigurationString,
    getLocalization,
    getAccount,
    getImageAndMask,
} from "../../store/sagas/selectors";
import InputFloatLabel from "../Form/InputFloatLabel";
import { InputData } from "../../models/summary/Config";
import MailIcon from "../../icons/MailIcon.svg";
import { KeyboardContext } from "../../utilities/virtual-keyboard";
import { SET_VIRTUAL_KEYBOARD } from "../../store/actions/sessionActions";
import store from "../../store/store";
import { sanitizeHtml } from "../../utilities/htmlSanitizer";
import { getPublicArticleNumber } from "../../utilities/itemUtilities";
import { buildSummaryPdfUrl } from "../../utilities/summaryPdfUrl";
import { buildSendToParentPayload } from "./sendToParentPayload";
import {
    AppendixPolicy,
    ConsentSettings,
    appendixKey,
    buildConsentOptions,
    collectSelectedConsents,
    isAppendixEnabled,
} from "./consentLogic";
import { useConsentSelection } from "./useConsentSelection";

// This card is the send-to-parent exit. It has its own DS Web Admin consent section: until NGOP-1508 it
// borrowed the send-to-shop wording, which meant one set of texts had to serve two different audiences
// (the parent is the customer's own page/CRM, not the end consumer). Like send-to-shop it records its
// appendix whenever configured, ticked or not — see AppendixPolicy.
const CONSENT_POLICY: AppendixPolicy = { section: "parent", appendixRequiresTick: false };

const mailformat = /^[^@]+@[^@]+\.[^@]+$/;

interface SpanProps {
    required?: Boolean;
}

const FormContainer = styled.div`
    display: flex;
    flex-flow: column;
    align-items: flex-start;
    width: 100%;
    color: ${props => props.theme.textColorSecondary};
`;

const Checkbox = styled.input`
    display: none;
`;

const CheckBoxStyled = styled.span<CheckboxProps>`
    width: 20px;
    height: 20px;
    background: #eee;
    border-radius: 4px;
    ${props => (props.error ? "border: solid thin red;" : "")}
    :after {
        content: "";
        position: absolute;
        display: none;
        left: 6px;
        top: 2px;
        width: 5px;
        height: 10px;
        border: solid white;
        border-width: 0 3px 3px 0;
        transform: rotate(45deg);
    }
`;

const CheckboxLabel = styled.label`
    font-family: ${props => props.theme.fontFamily};
    font-size: 12px;
    align-self: flex-start;
    color: ${props => props.theme.textColorSecondary};
    margin: 2px;
    margin-top: 8px;
    text-align: left;
    display: flex;
    position: relative;
    cursor: pointer;

    &:hover input ~ ${CheckBoxStyled} {
        background: ${({ theme }) => get(theme, "summary.card.button.hoverColor", "#223861")};
    }

    input:checked + ${CheckBoxStyled} {
        background: ${({ theme }) => get(theme, "summary.card.button.backgroundColor", "#223861")};
        :after {
            display: block;
        }
    }
    ${({ theme }) =>
        theme.bp.xxxl`
                    font-size: 1em;
                `}
`;

const CheckSpan = styled.span<SpanProps>`
    position: relative;
    margin-left: 5px;
    max-width: calc(100% - 35px);

    ${props =>
        props.required
            ? `
        margin-left: 12px;
        ::before {
            position: absolute;
            left: -8px;
            content: '*';
        }`
            : ``}
`;

const FormTextarea = styled.textarea<FormTextareaProps>`
    font-family: ${props => props.theme.fontFamily};
    ${({ theme }) => theme.bp.xs`
        font-size: 14px;
    `};
    ${({ theme }) => theme.bp.xl`
        font-size: 14px;
    `};
    ${({ theme }) =>
        theme.bp.xxxl`
                    font-size: 1.2em;
                `}

    max-width: calc(100% - 20px);
    width: calc(100% - 20px);
    padding: 10px 10px;
    height: 4em;
    border-radius: 4px;
    border: 1px solid ${props => (props.error ? "red" : "#d0d0d0")};
    transition: box-shadow 0.2s ease-in-out;
    outline: none;

    @media (hover) {
        &:focus {
            box-shadow: 0px 0px 0px 1px #ccc inset;
        }
        &:hover {
            box-shadow: 0px 0px 0px 1px #ccc inset;
        }
    }

    &::placeholder {
        font-size: 14px;
    }
`;

interface FormTextareaProps {
    error?: Boolean;
}

const LabelContainer = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
`;

const FormError = styled.span`
    font-family: ${props => props.theme.fontFamily};
    font-size: 12px;
    align-self: flex-start;
    color: red;
    margin-top: -8px;
    margin-bottom: 10px;
    ${({ theme }) =>
        theme.bp.xxxl`
                    font-size: 1em;
                `}
`;

const Container = styled.div<ContainerProps>`
    visibility: ${props => (props.visible ? "visible" : "hidden")};
`;

const FeedbackContainer = styled.div<ContainerProps>`
    visibility: ${props => (props.visible ? "visible" : "hidden")};
    ${({ theme }) => theme.bp.xs`
        width: calc(100% - 44px);
        padding: 20px;
        height: calc(100% - 40px);
    `};
    ${({ theme }) => theme.bp.sm`
        width: calc(100% - 24px);
        padding: 10px;
        height: calc(100% - 20px);
    `};
    ${({ theme }) => theme.bp.xxl`
        width: calc(100% - 64px);
    `};
    position: absolute;
    bottom: 0;
    padding: 10px;
    width: calc(100% - 64px);
    height: calc(100% - 20px);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: space-between;
`;

const FeedbackContent = styled.div``;

const SentIcon = styled.div`
    margin-top: 15px;
    svg {
        fill: currentColor;
        color: var(--primary-color);
`;

interface ContainerProps {
    visible: Boolean;
}

interface CheckboxProps {
    error: Boolean;
}

const SendCustomSummaryCardToParent = ({ config }: { config: any }) => {
    const dispatch = useDispatch();
    const sessionId = useSelector(getSessionId);
    const frontendSettings = useSelector(getFrontendSettings);
    const showPrice = useSelector(getShowPrice);
    const appConfig = useSelector(getAppConfig);
    const language = useSelector(getLanguage);
    const guiTranslations = useSelector(getGuiTranslations);
    const appCustomization = useSelector(getAppCustomization);
    const summaryGroups = useSelector(getCurrentSummaries);
    const totalPrice = useSelector(getTotalPrice);
    const totalPriceText = useSelector(getTotalPriceText);
    const localization = useSelector(getLocalization);
    const account = useSelector(getAccount);
    const imageAndMask = useSelector(getImageAndMask);
    const touchScreen = useSelector(getTouchScreen);
    const VirtualKeyboard = useContext(KeyboardContext);
    const allItems = summaryGroups.flatMap(g => g.items);

    // appCustomization is null for sessions without an account (no customization.json) — guard so the card degrades to no consent boxes instead of crashing.
    const consentSettings = (appCustomization?.FrontendSettings || {}) as ConsentSettings;
    const consentOptions = buildConsentOptions(consentSettings, CONSENT_POLICY.section);
    const appendixEnabled = isAppendixEnabled(consentSettings, CONSENT_POLICY.section);

    const [userInputs, setUserInputs] = useState<any>({});
    const [errors, setErrors] = useState<any>({});
    const [isDisabled, setIsDisabled] = useState(false);
    const [dataSent, setDataSent] = useState(false);
    const { checkedConsents, consentErrors, toggleConsent, validateRequiredConsents } =
        useConsentSelection(consentOptions);

    const inputFields: InputData[] = config.inputs?.filter((input: InputData) => input.enabled !== false) || [];

    const checkIfPossibleToSendEventToParent = () => {
        let isValid = true;
        const inputErrors: { [key: string]: string } = {};

        // Check user inputs
        inputFields.forEach(field => {
            if (field.id === "email" && userInputs.email && !userInputs.email.match(mailformat)) {
                isValid = false;
                inputErrors[field.id] = get(appConfig, `translations.${language}.sendToShopInvalidEmail`);
            } else if (field.required && !userInputs[field.id]) {
                isValid = false;
                inputErrors[field.id] = get(appConfig, `translations.${language}.sendToShopRequiredField`, "Required");
            }
        });

        // Required consents left unticked get a red outline and block the send.
        if (!validateRequiredConsents()) {
            isValid = false;
        }

        setErrors(inputErrors);
        return isValid;
    };

    const prepareDataForPost = () => {
        const currentUrl = new URL(window.location.href);
        const state = store.getState();
        const configString = getConfigurationString(state);

        if (configString !== null) {
            currentUrl.searchParams.set("config_string", configString);
        }

        // The CRM stores the wording the user actually saw, not just the id, so each id is paired with
        // its rendered text here rather than in the shared collector (the other two exits post ids only).
        const consents = collectSelectedConsents(consentOptions, checkedConsents, appendixEnabled, CONSENT_POLICY).map(
            id => ({ id, text: guiTranslations.get(id) })
        );

        // Full PDF for the CRM: prices/info included regardless of what the page shows (the parent is
        // the customer's own page/CRM, not the end consumer).
        const pdfUrl = configString
            ? buildSummaryPdfUrl(configString, {
                  accountId: account?.id,
                  market: localization,
                  language,
                  hidePrice: false,
              })
            : undefined;

        return buildSendToParentPayload({
            userInputs,
            consents,
            siteUrl: currentUrl.toString(),
            summary: allItems.map(x => ({
                menuName: x.menuText,
                itemName: x.name,
                articleNumber: getPublicArticleNumber(x.articleNumber),
            })),
            totalPrice: totalPriceText,
            configurationImageUrl: imageAndMask?.configurationImagePath ?? undefined,
            pdfUrl,
        });
    };

    const sendToParent = async () => {
        try {
            if (!sessionId || !frontendSettings) return;
            const isValid = checkIfPossibleToSendEventToParent();
            if (!isValid) return;

            setIsDisabled(true);

            const postData = prepareDataForPost();

            window.parent.postMessage(postData, "*");

            setDataSent(true);

            GaReporter.event("cta_send_to_parent", "CTA");
            GaReporter.trackProductCheckout(
                GaEcommerceAction.Purchase,
                "SEND_TO_PARENT",
                allItems,
                showPrice ? totalPrice : null
            );
        } catch (e) {
            setIsDisabled(false);
            dispatch({
                type: SET_ERROR,
                error: {
                    // sendToParentFailure is not defined in the translation files; reuse the
                    // existing sendToShopFailure key (matches the former PDF card's fallback).
                    message: get(appConfig, `translations.${language}.sendToShopFailure`),
                },
            });
        }
    };

    const renderInputField = (input: InputData) => {
        const setUserInput = (value: string) => {
            setUserInputs({ ...userInputs, [input.id]: value });
        };

        if (input.id === "message") {
            const onFormFocus = (value: boolean, currentValue: string) => {
                if (!touchScreen) return;

                if (value) {
                    VirtualKeyboard.show(inputs => setUserInput(inputs), currentValue);
                } else {
                    VirtualKeyboard.hide();
                }

                dispatch({ type: SET_VIRTUAL_KEYBOARD, showVirtualKeyboard: value });
            };

            // Wrapped like the labelled inputs below so the message carries its error text and
            // not only the red outline. The textarea names itself through a placeholder, which
            // disappears as soon as anything is typed, so the outline alone leaves a
            // filled-but-rejected message with nothing saying what is wrong.
            return (
                <LabelContainer key={input.id}>
                    <FormTextarea
                        value={userInputs[input.id]}
                        error={errors[input.id]}
                        onChange={event => setUserInput(event.target.value)}
                        placeholder={input.required ? `*${input.title}` : input.title}
                        onFocus={event => onFormFocus(true, event.target.value)}
                        onBlur={event => onFormFocus(false, event.target.value)}
                    />
                    {errors[input.id] ? <FormError>{errors[input.id]}</FormError> : null}
                </LabelContainer>
            );
        }

        return (
            <LabelContainer key={input.id}>
                <InputFloatLabel
                    name={input.title}
                    error={errors[input.id]}
                    setEvent={setUserInput}
                    required={input.required}
                />
                {errors[input.id] ? <FormError>{errors[input.id]}</FormError> : null}
            </LabelContainer>
        );
    };

    return (
        <Card>
            <Container visible={!dataSent}>
                <CardTitle>{config.title}</CardTitle>
                {config.description ? (
                    <CardDescription dangerouslySetInnerHTML={{ __html: sanitizeHtml(config.description) }} />
                ) : (
                    <></>
                )}

                <FormContainer>
                    {inputFields.map(inputData => renderInputField(inputData))}

                    {consentOptions.map(option => (
                        <CheckboxLabel key={option.key}>
                            <Checkbox
                                type="checkbox"
                                checked={!!checkedConsents[option.key]}
                                onChange={toggleConsent(option.key)}
                                aria-invalid={consentErrors.includes(option.key)}
                            />
                            <CheckBoxStyled error={consentErrors.includes(option.key)} />
                            <CheckSpan required={option.required}>{guiTranslations.get(option.key)}</CheckSpan>
                        </CheckboxLabel>
                    ))}

                    {/* The appendix is informational text (no checkbox); recorded whenever enabled. */}
                    {appendixEnabled ? (
                        <ConsentSpan>{guiTranslations.get(appendixKey(CONSENT_POLICY.section))}</ConsentSpan>
                    ) : null}
                </FormContainer>
                <CardButton
                    isDisabled={isDisabled}
                    onClick={() => {
                        sendToParent();
                    }}
                >
                    <p>{config.buttonText}</p>
                </CardButton>
            </Container>

            <FeedbackContainer visible={dataSent}>
                <CardTitle>{config.title}</CardTitle>
                <FeedbackContent>
                    <SentIcon>
                        <MailIcon width={40} />
                    </SentIcon>
                    <CardDescription>{config.emailSentDescription}</CardDescription>
                </FeedbackContent>
                <SecondaryCardButton
                    onClick={() => {
                        setDataSent(false);
                        setIsDisabled(false);
                    }}
                >
                    <p>{config.feedbackButtonText}</p>
                </SecondaryCardButton>
            </FeedbackContainer>
        </Card>
    );
};

export default SendCustomSummaryCardToParent;
